import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { ILead } from 'src/services/api/leads/types';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { queryKeys } from 'src/services/api/constants';
import { getApiErrorMessage } from 'src/services/api/utils';
import { reconcileCoapplicantCollection } from 'src/services/api/leads/coapplicants';

type IAssociateLeadsResponse = {
    leads: ILead[];
}

type IAssociatedLeadsRequest = {
    lead_uuids: string[];
}

export const useAssociateLeads = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ILead[],
        IStandardErrorResponse,
        IAssociatedLeadsRequest
    >(async (payload) => {
        const path = `${GET_LEADS_PATH}/coapplicants`;
        const res = await apiClient(path, {
            method: 'post',
            json: payload,
        }).json<IAssociateLeadsResponse>();
        return res.leads;
    }, {
        onSuccess: (data) => {
            if (!data || data.length === 0) {
                addNotification('Leads associated', 'success');
                return;
            }

            const cachedLeads = queryClient.getQueryData<ILead[]>(queryKeys.leads);
            const reconciled = reconcileCoapplicantCollection(
                cachedLeads,
                data,
                new Set(),
            );
            queryClient.setQueryData<ILead[]>(queryKeys.leads, reconciled);
            const cachedByUuid = new Map(cachedLeads?.map((lead) => [lead.uuid, lead]));
            reconciled.forEach((lead) => {
                if (cachedByUuid.get(lead.uuid) !== lead) {
                    queryClient.setQueryData<ILead>([queryKeys.leads, lead.uuid], lead);
                }
            });

            addNotification('Leads associated', 'success');
        },
        onError: (error) => {
            const errorMessage = getApiErrorMessage(error) || 'Failed to associate leads';
            addNotification(errorMessage, 'error');
        }
    });
};
